import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Card } from "react-bootstrap";
import { fetchIncomeStatementData } from "../../apis/Stock";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toMarginValue = (item) => {
  const revenue = toNumberOrNull(item?.revenue);
  const netIncome = toNumberOrNull(item?.netIncome);
  if (!revenue) return null;

  return (netIncome / revenue) * 100;
};

const buildPeriodLabel = (value, period) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "");

  if (period === "annual") {
    return String(date.getFullYear());
  }

  const year = String(date.getFullYear()).slice(2);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${year}Q${quarter}`;
};

// 순이익/매출 차트
export default function RevenueNetIncomeBarChart({ ticker, selectedPeriod = "quarter" }) {
  const [chartDataByPeriod, setChartDataByPeriod] = useState({ quarter: null, annual: null });
  const [isLoading, setIsLoading] = useState(false);
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    if (!ticker) {
      setChartDataByPeriod({ quarter: null, annual: null });
      setIsLoading(false);
      return;
    }

    setChartDataByPeriod({ quarter: null, annual: null });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    if (chartDataByPeriod[selectedPeriod] !== null) {
      return;
    }

    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetchIncomeStatementData(ticker, selectedPeriod);
        if (!response.ok) {
          throw new Error("Failed to fetch income statement");
        }

        const data = await response.json();
        if (!isMounted) return;

        setChartDataByPeriod((prev) => ({
          ...prev,
          [selectedPeriod]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        console.error("Error fetching income statement:", error);
        if (isMounted) {
          setChartDataByPeriod((prev) => ({
            ...prev,
            [selectedPeriod]: [],
          }));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chartDataByPeriod, selectedPeriod, ticker]);

  const chartData = useMemo(
    () => (Array.isArray(chartDataByPeriod[selectedPeriod]) ? [...chartDataByPeriod[selectedPeriod]] : [])
      .filter((item) => item?.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [chartDataByPeriod, selectedPeriod]
  );

  const categories = useMemo(
    () => chartData.map((item, index) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return buildPeriodLabel(item.date, selectedPeriod);

      const currentYear = date.getFullYear();
      if (selectedPeriod === "annual") {
        return String(currentYear);
      }

      const previousRow = index > 0 ? chartData[index - 1] : null;
      const previousYear = previousRow?.date ? new Date(previousRow.date).getFullYear() : null;
      return currentYear !== previousYear ? String(currentYear) : "";
    }),
    [chartData, selectedPeriod]
  );

  const series = useMemo(
    () => [
      {
        name: "매출",
        type: "bar",
        data: chartData.map((item) => toNumberOrNull(item?.revenue)),
      },
      {
        name: "순이익",
        type: "bar",
        data: chartData.map((item) => toNumberOrNull(item?.netIncome)),
      },
      {
        name: "이익률",
        type: "line",
        data: chartData.map((item) => toMarginValue(item)),
      },
    ],
    [chartData]
  );

  const hasData = useMemo(
    () => series.some((entry) => entry.data.some((value) => value !== null)),
    [series]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        animations: { enabled: false },
        background: chartColors.panelBg,
      },
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 0.1,
          },
        },
      },
      colors: ["#0F6FE0", "#dd041e", "rgb(255, 69, 0)"],
      responsive: [
        {
          breakpoint: 576,
          options: {
            xaxis: {
              labels: {
                rotate: -90,
                rotateAlways: true,
              },
            },
          },
        },
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "62%",
          borderRadius: 4,
          borderRadiusApplication: "end",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "straight",
        width: [0, 0, 2.5],
      },
      markers: {
        size: [0, 0, 0],
        hover: {
          size: 3,
        },
      },
      fill: {
        opacity: [1, 1, 1],
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        floating: false,
        clusterGroupedSeries: false,
        labels: {
          colors: chartColors.legendColor,
        },
        itemMargin: {
          horizontal: 12,
          vertical: 0,
        },
      },
      title: {
        text: "순이익/매출",
        align: "center",
        offsetY: 10,
        floating: false,
        style: {
          color: chartColors.legendColor,
          fontSize: "18px",
          fontWeight: 700,
        },
      },
      grid: {
        borderColor: chartColors.gridColor,
        strokeDashArray: 4,
        padding: {
          top: 2,
          right: 0,
          bottom: 2,
          left: 0,
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
          bottom: 2,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        {
          seriesName: ["매출", "순이익"],
          labels: {
            style: {
              colors: chartColors.tickColor,
              fontSize: "11px",
              padding: {
                left: 0,
                right: 0
              }
            },
            formatter: (value) => {
              const numeric = Number(value);
              if (!Number.isFinite(numeric)) return "-";

              const absValue = Math.abs(numeric);
              const sign = numeric < 0 ? "-" : "";

              if (absValue >= 2_000_000_000_000) {
                return `${sign}$${(absValue / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "")}T`;
              }
              if (absValue >= 2_000_000_000) {
                return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
              }
              if (absValue >= 2_000_000) {
                return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
              }

              return `${sign}$${absValue.toFixed(0)}`;
            },
          },
        },
        {
          seriesName: "이익률",
          opposite: true,
          labels: {
            minWidth: 34,
            maxWidth: 34,
            offsetX: -6,
            style: {
              colors: chartColors.tickColor,
              fontSize: "11px",
            },
            formatter: (value) => `${Number(value).toFixed(0)}%`,
          },
        },
      ],
      tooltip: {
        theme: isDarkMode ? "dark" : "light",
        shared: true,
        intersect: false,
        x: {
          formatter: (_, context) => chartData?.[context.dataPointIndex]?.date || "",
        },
        y: {
          formatter: (value, context) => {
            const { seriesIndex, dataPointIndex } = context;
            if (seriesIndex === 2) return `${Number(value).toFixed(1)}%`;

            const item = chartData?.[dataPointIndex];
            if (!item) return "-";

            if (seriesIndex === 0) {
              const numeric = toNumberOrNull(item?.revenue);
              if (!Number.isFinite(numeric)) return "-";

              const absValue = Math.abs(numeric);
              const sign = numeric < 0 ? "-" : "";

              if (absValue >= 1_000_000_000_000) {
                return `${sign}$${(absValue / 1_000_000_000_000).toFixed(2).replace(/\.00$/, "")}T`;
              }
              if (absValue >= 1_000_000_000) {
                return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
              }
              if (absValue >= 1_000_000) {
                return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
              }
              if (absValue >= 1_000) {
                return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
              }

              return `${sign}$${absValue.toFixed(0)}`;
            }

            const numeric = toNumberOrNull(item?.netIncome);
            if (!Number.isFinite(numeric)) return "-";

            const absValue = Math.abs(numeric);
            const sign = numeric < 0 ? "-" : "";

            if (absValue >= 1_000_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000_000).toFixed(2).replace(/\.00$/, "")}T`;
            }
            if (absValue >= 1_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
            }
            if (absValue >= 1_000_000) {
              return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
            }
            if (absValue >= 1_000) {
              return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
            }

            return `${sign}$${absValue.toFixed(0)}`;
          },
        },
      },
      noData: {
        text: "데이터가 없습니다.",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: chartColors.tickColor,
        },
      },
    }),
    [categories, chartColors, chartData, isDarkMode]
  );

  if (isLoading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderRadius: "12px", color: chartColors.tickColor }}
      >
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <Card
        className="d-flex align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
      >
        데이터가 없습니다.
      </Card>
    );
  }

  return (
    <Card className="w-full h-full"
      style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}>
      <Chart options={options} series={series} type="bar" height={300} />
    </Card>
  );
}
